import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ContentChild,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  TemplateRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  BrowserPermissionResultStatus,
  BrowserPermissions,
  BrowserPermissionStates,
} from '@freelancer/browser-permissions';
import {
  MediaDeviceErrorCode,
  MediaDeviceResultStatus,
  MediaDevices,
} from '@freelancer/media-devices';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconSize } from '@freelancer/ui/icon';
import * as Rx from 'rxjs';
import { readImageDataIntoFile } from '../camera';
import { CameraModalComponent } from '../camera-modal/camera-modal.component';
import { ModalSize } from '../modal/modal-size';
import { ModalService } from '../modal/modal.service';

const DEFAULT_VIDEO_CONSTRAINTS = {
  aspectRatio: 1.142857143,
};

@Component({
  selector: 'fl-camera-input',
  template: `
    <fl-button
      i18n="Take a photo button"
      class="PhotoBtn"
      [color]="ButtonColor.DEFAULT"
      [size]="ButtonSize.SMALL"
      [disabled]="disabled"
    >
      <fl-icon [name]="'ui-camera-outline'" [size]="IconSize.MID"></fl-icon>
      Take Photo
    </fl-button>
  `,
  styleUrls: ['../camera-modal/camera-modal.component.scss'],
})
export class CameraInputComponent implements OnInit {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  IconSize = IconSize;

  fileSelectControl: FormControl;

  @Input() disabled = false;
  @Input() videoConstraints: MediaTrackConstraints = {};
  @Output() canActivate = new EventEmitter<boolean>();
  @Output() onError = new EventEmitter<MediaDeviceErrorCode>();
  @Output() takePhoto = new EventEmitter<File>();

  hasVideoDevices = false;

  @ContentChild('content') content: TemplateRef<CameraInputComponent>;

  private videoDevices: ReadonlyArray<MediaDeviceInfo> = [];
  private videoDeviceConstraints: MediaTrackConstraints;
  private cameraFileSubject$ = new Rx.Subject<File>();

  cameraFile$ = this.cameraFileSubject$.asObservable();

  constructor(
    private mediaDevices: MediaDevices,
    private browserPermission: BrowserPermissions,
    private modalService: ModalService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  ngOnInit() {
    this.videoDeviceConstraints = {
      ...DEFAULT_VIDEO_CONSTRAINTS,
      ...this.videoConstraints,
    };
    this.setVideoDevices();
  }

  private setVideoDevices() {
    if (isPlatformBrowser(this.platformId)) {
      this.mediaDevices
        .getVideoDevices()
        .then(result => {
          if (result.status === MediaDeviceResultStatus.SUCCESS) {
            this.videoDevices = result.devices;
            this.hasVideoDevices = result.devices.length > 0;
            this.handleCameraActivate(this.hasVideoDevices);
          }
        })
        .catch(() => {
          this.hasVideoDevices = false;
          this.handleCameraActivate(false);
        });
    }
  }

  @HostListener('click')
  showModal() {
    if (!this.hasVideoDevices || this.disabled) {
      return false;
    }

    let browserPermissionModalVisibile = false;
    const device = this.videoDevices[0];
    const videoConstraint = {
      video: {
        deviceId: device.deviceId,
      },
    };

    this.showBrowserPermissionModal()
      .then(result => {
        browserPermissionModalVisibile = result;
        return this.mediaDevices.getUserMedia(videoConstraint);
      })
      .then(result => {
        if (result.status === MediaDeviceResultStatus.SUCCESS) {
          return this.mediaDevices.stopMediaStream(result.stream);
        }

        throw new Error(result.error);
      })
      .then(() => {
        if (!browserPermissionModalVisibile) {
          this.handleCameraModal();
        }
      })
      .catch(error => {
        this.handleOnError(error);
        this.hasVideoDevices = false;
      });
  }

  private handleCameraActivate(canActivate: boolean) {
    this.canActivate.emit(canActivate);
  }

  private handleCameraModal() {
    this.modalService
      .open(CameraModalComponent, {
        inputs: {
          videoDevices: this.videoDevices,
          videoConstraints: this.videoDeviceConstraints,
          content: this.content,
        },
        mobileFullscreen: true,
        size: ModalSize.MID,
      })
      .afterClosed()
      .toPromise()
      .then(result => {
        if (result) {
          this.takePhoto.emit(readImageDataIntoFile(result));
        }
      });
  }

  private handleOnError(error: MediaDeviceErrorCode) {
    this.onError.emit(error);
  }

  private showBrowserPermissionModal(): Promise<boolean> {
    return this.browserPermission
      .getCameraPermissions()
      .then(result => {
        if (
          result.status === BrowserPermissionResultStatus.SUCCESS &&
          result.permissionStatus.state === BrowserPermissionStates.PROMPT
        ) {
          const permission = result.permissionStatus;
          permission.onchange = () => {
            if (permission.state === BrowserPermissionStates.GRANTED) {
              this.handleCameraModal();
            } else {
              this.modalService.close();
            }
          };
          return true;
        }
        return false;
      })
      .catch(() => false);
  }
}
